import React from 'react';
import { useLoansList } from './hooks/useLoansList';
import SharedFilters from '../../../components/SharedFilters';
import AppPagination from '../../../components/AppPagination';
import LoansTable from './components/LoansTable';

export default function LoansListScreen() {
  const { state, actions } = useLoansList();

  const statusTabs = [
    { key: 'Pending', label: 'Pending' },
    { key: 'Approved', label: 'Approved' },
    { key: 'Rejected', label: 'Rejected' },
    { key: 'All', label: 'All' },
  ];

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Header Banner with Title on Left, Status Toggle Filter & Primary CTA on Right */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.02em] text-on-surface">
            Loan Application
          </h1>
          <p className="text-xs text-on-surface-variant font-medium mt-1">
            Staff loan disbursements, EMI recovery schedules, and advance tracking
          </p>
        </div>

        {/* Actions Cluster: Segmented Status Tabs (No local Add button; use global Top Navbar '+ Create New') */}
        <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-surface-container-high w-fit shadow-xs">
          {statusTabs.map((tab) => {
            const isActive = state.statusFilter === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => actions.handleStatusTabChange(tab.key)}
                className={`px-5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer select-none ${
                  isActive
                    ? 'bg-primary text-on-primary shadow-sm'
                    : 'text-on-surface-variant hover:text-on-surface hover:bg-surface-container-highest/60'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </section>

      {/* Shared Filters Component */}
      <SharedFilters
        searchField={state.searchField}
        searchText={state.searchText}
        statusFilter={state.statusFilter}
        showStatus={false}
        companyFilter={state.companyFilter}
        branchFilter={state.branchFilter}
        departmentFilter={state.departmentFilter}
        fromDate={state.fromDate}
        toDate={state.toDate}
        setSearchField={actions.setSearchField}
        setSearchText={actions.setSearchText}
        setCompanyFilter={actions.setCompanyFilter}
        setBranchFilter={actions.setBranchFilter}
        setDepartmentFilter={actions.setDepartmentFilter}
        setFromDate={actions.setFromDate}
        setToDate={actions.setToDate}
        onSearch={actions.handleSearch}
        onReset={actions.resetFilters}
        searchFieldOptions={state.searchFieldOptions}
        visibleAccordionFilters={['company', 'branch', 'department', 'fromDate', 'toDate']}
        tools={state.tools}
        onToolSelect={actions.handleToolSelect}
      />

      {/* Data Table Section */}
      <section className="space-y-4">
        <LoansTable
          records={state.records}
          isLoading={state.isLoading}
          onApprove={actions.handleApprove}
          onReject={actions.handleReject}
          onDelete={actions.handleDelete}
          onResetFilters={actions.resetFilters}
        />

        {/* Table Footer with Pagination */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row bg-surface-container-lowest rounded-2xl shadow-xs">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing {state.totalCount === 0 ? 0 : (state.page - 1) * state.pageSize + 1} to{' '}
            {Math.min(state.page * state.pageSize, state.totalCount)} of {state.totalCount} entries
          </p>
          <AppPagination
            page={state.page}
            totalPages={state.totalPages}
            onPageChange={actions.setPage}
            onPrevious={() => actions.setPage((p) => Math.max(p - 1, 1))}
            onNext={() => actions.setPage((p) => Math.min(p + 1, state.totalPages))}
            canPrevious={state.page > 1}
            canNext={state.page < state.totalPages}
          />
        </div>
      </section>
    </div>
  );
}
